import React, { useEffect, useRef, useState } from "react";
import { FiX } from "react-icons/fi";
import toast from "react-hot-toast";
import api from "@/services/api";

/**
 * Global screening progress for a job — polls /application/screening/status with
 * { filter: { job_id } } whenever there's active work, hides itself when idle.
 * Selection-independent: works even if the user deselects or another recruiter queued.
 *
 * Props:
 *   jobId   – the job to watch
 *   pulse   – optional counter; bumping it triggers an immediate status re-fetch
 *             (useful when the parent knows something might have changed)
 */
const POLL_INTERVAL_MS = 2500;

export default function ScreeningProgressBadge({ jobId, pulse = 0 }) {
  const [counts, setCounts] = useState({ pending: 0, in_progress: 0, done: 0, error: 0, total: 0 });
  const [cancelling, setCancelling] = useState(false);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => () => {
    mountedRef.current = false;
    clearTimeout(timerRef.current);
  }, []);

  useEffect(() => {
    if (!jobId) return;
    clearTimeout(timerRef.current);

    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const res = await api.post("/application/screening/status", { filter: { job_id: jobId } });
        if (cancelled || !mountedRef.current) return;
        if (!res?.ok) return;
        const c = res.counts || {};
        const next = {
          pending: c.pending || 0,
          in_progress: c.in_progress || 0,
          done: c.done || 0,
          error: c.error || 0,
          total: (c.pending || 0) + (c.in_progress || 0) + (c.done || 0) + (c.error || 0),
        };
        setCounts(next);
        if (next.pending + next.in_progress > 0) {
          timerRef.current = setTimeout(fetchStatus, POLL_INTERVAL_MS);
        }
      } catch (e) {
        // silent — try again on next pulse
      }
    };
    fetchStatus();

    return () => {
      cancelled = true;
      clearTimeout(timerRef.current);
    };
  }, [jobId, pulse]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const res = await api.post("/application/screening/cancel", { filter: { job_id: jobId } });
      if (res?.ok) toast.success(`Cancelled ${res.cancelled || 0} pending`);
    } catch (e) {
      toast.error("Failed to cancel");
    } finally {
      if (mountedRef.current) setCancelling(false);
    }
  };

  const active = counts.pending + counts.in_progress;
  if (active === 0) return null;

  const finished = counts.done + counts.error;
  const total = active + finished;
  const pct = total > 0 ? Math.round((finished / total) * 100) : 0;

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-50 border border-indigo-100 text-indigo-700">
      <span className="text-xs font-semibold">⚡ Screening</span>
      <div className="flex items-center gap-2 min-w-[140px]">
        <div className="flex-1 h-1.5 bg-indigo-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-indigo-500 rounded-full transition-all duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="text-xs font-medium whitespace-nowrap">
          {finished}/{total}
        </span>
      </div>
      {counts.error > 0 && (
        <span className="text-xs font-medium text-red-600 whitespace-nowrap">{counts.error} err</span>
      )}
      <button
        onClick={handleCancel}
        disabled={cancelling}
        title="Cancel pending"
        className="p-0.5 rounded hover:bg-indigo-100 text-indigo-500 hover:text-indigo-700 transition-colors disabled:opacity-50"
      >
        <FiX className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
